// 能源畫面的純邏輯（plan SPEC #energy-ui-common、NRG-V1–V9）：範圍 → 要查哪些組織、多份摘要合併、CSV、比較的顯示條件、
// 量測點顯示名、需要處理的排序。沒有 React、沒有 fetch，單元測試在 energy.test.ts。
import type { Attention, Carbon, EnergySummary, HomeRow, Money, NoCompareReason, OrgRow, RankRow, SeriesPoint } from './types';

// ---- 範圍 ----

export interface ScopeHomeLite {
  home_id: string;
  org_id?: string;
  api?: string;
  external?: boolean;
}

export interface EnergyTarget {
  org: string;
  api: string;
  /** 送給 app-api 的 scope（all / site:<id> / home:<id> / view:<id>） */
  scope: string;
}

/**
 * VMS 頁首範圍 → 要查的組織與 scope（SPEC #energy-api `GET /v1/orgs/{org}/energy/summary?scope=`）。
 *   - org:X → X 的 all；site:/node: → 那個組織的 site:<node>；home:<id> → 那個家戶所在組織的 home:<id>；view:<id> → 擁有檢視的組織
 *   - all 與其他（來源、分享者）→ 範圍內看得到的家戶依組織分組；只有部分家戶在範圍內時逐家戶查（不把範圍外的算進來）
 */
export function energyTargets(scope: string, visible: ScopeHomeLite[], allHomes: ScopeHomeLite[], orgApi: (org: string) => string | undefined): EnergyTarget[] {
  const [kind, ...rest] = scope.split(':');
  const id = rest.join(':');
  const homes = visible.filter((h) => h.org_id && !h.external);
  const apiOf = (org: string, home?: ScopeHomeLite) => home?.api ?? orgApi(org) ?? '';
  const byOrg = new Map<string, ScopeHomeLite[]>();
  for (const h of homes) byOrg.set(h.org_id!, [...(byOrg.get(h.org_id!) ?? []), h]);
  switch (kind) {
    case 'org':
      return byOrg.has(id) || !homes.length ? [{ org: id, api: apiOf(id, byOrg.get(id)?.[0]), scope: 'all' }] : [];
    case 'site':
    case 'node': {
      const node = kind === 'node' ? rest.slice(1).join(':') : id;
      const org = kind === 'node' ? rest[0] : (homes[0]?.org_id ?? '');
      return org ? [{ org, api: apiOf(org, byOrg.get(org)?.[0]), scope: node ? 'site:' + node : 'all' }] : [];
    }
    case 'home': {
      const h = allHomes.find((x) => x.home_id === id);
      return h?.org_id ? [{ org: h.org_id, api: apiOf(h.org_id, h), scope: 'home:' + id }] : [];
    }
    case 'view': {
      const org = homes[0]?.org_id;
      return org ? [{ org, api: apiOf(org, homes[0]), scope: 'view:' + id }] : [];
    }
  }
  const out: EnergyTarget[] = [];
  for (const [org, hs] of byOrg) {
    const all = allHomes.filter((h) => h.org_id === org && !h.external);
    if (kind === 'all' || hs.length === all.length) out.push({ org, api: apiOf(org, hs[0]), scope: 'all' });
    else for (const h of hs) out.push({ org, api: apiOf(org, h), scope: 'home:' + h.home_id });
  }
  return out;
}

// ---- 多份摘要合併（範圍跨組織、或逐家戶查） ----

export function addMoney(list: Money[], m: Money): void {
  const x = list.find((y) => y.currency === m.currency);
  if (x) x.amount += m.amount;
  else list.push({ currency: m.currency, amount: m.amount });
}

function mergeSeries(a: SeriesPoint[], b: SeriesPoint[]): SeriesPoint[] {
  if (!a.length) return b.map((p) => ({ ...p }));
  // 同一期間的格數一樣（依位置合併；跨時區的範圍用各地點當地日期，SPEC #energy-terms）
  return a.map((p, i) => {
    const q = b[i];
    if (!q) return p;
    const mwh = p.mwh === null && q.mwh === null ? null : (p.mwh ?? 0) + (q.mwh ?? 0);
    const coverage = (p.coverage + q.coverage) / 2;
    const quality = p.quality === 'incomplete' && q.quality === 'incomplete' ? 'incomplete' : p.quality === 'measured' && q.quality === 'measured' ? 'measured' : 'estimated';
    const by_kind = p.by_kind || q.by_kind ? { ...p.by_kind } : undefined;
    if (by_kind && q.by_kind) for (const [k, v] of Object.entries(q.by_kind)) by_kind[k as keyof typeof by_kind] = (by_kind[k as keyof typeof by_kind] ?? 0) + (v ?? 0);
    return { ...p, mwh, coverage, quality, partial: p.partial || q.partial, by_kind };
  });
}

/** 依{leaf}表 → 依組織表（client 合併時用；平台有 by_org 時照平台）。 */
export function orgRowsOf(rows: HomeRow[], days: number): OrgRow[] {
  const m = new Map<string, OrgRow>();
  for (const r of rows) {
    const k = r.org_id ?? '';
    const o =
      m.get(k) ??
      ({
        org_id: k,
        homes: 0,
        homes_with_meters: 0,
        mwh: 0,
        comparison: { ratio: null, prev_mwh: 0 },
        daily_avg_mwh: 0,
        costs: [],
        no_tariff_homes: 0,
        meters: 0,
        unavailable_meters: 0,
        incomplete_hours: 0,
      } as OrgRow & { _cur?: number });
    o.homes++;
    if (r.meters) o.homes_with_meters++;
    o.mwh += r.mwh;
    if (r.comparison && o.comparison) {
      o.comparison.prev_mwh += r.comparison.prev_mwh;
      (o as OrgRow & { _cur?: number })._cur = ((o as OrgRow & { _cur?: number })._cur ?? 0) + r.mwh;
    }
    for (const c of r.costs) addMoney(o.costs, c);
    if (r.no_tariff) o.no_tariff_homes++;
    o.meters += r.meters;
    o.unavailable_meters += r.unavailable_meters;
    o.incomplete_hours += r.incomplete_hours;
    m.set(k, o);
  }
  return [...m.values()].map((o) => {
    const cur = (o as OrgRow & { _cur?: number })._cur ?? 0;
    const prev = o.comparison?.prev_mwh ?? 0;
    const { _cur: _, ...rest } = o as OrgRow & { _cur?: number };
    void _;
    return { ...rest, daily_avg_mwh: o.mwh / Math.max(1, days), comparison: prev >= 1e6 ? { ratio: (cur - prev) / prev, prev_mwh: prev } : prev > 0 ? { ratio: null, prev_mwh: prev } : null };
  });
}

/** 多份摘要 → 一份（數字相加、幣別與碳排口徑分列、比較用同一組量測點重算）。 */
export function mergeSummaries(list: EnergySummary[]): EnergySummary | null {
  if (!list.length) return null;
  if (list.length === 1) return list[0];
  const out: EnergySummary = {
    ...list[0],
    mwh: 0,
    forecast_mwh: 0,
    cost_by_currency: [],
    carbon: [],
    coverage: 0,
    incomplete_hours: 0,
    incomplete_meters: 0,
    meters: 0,
    poe_meters: 0,
    comparison: null,
    homes_without_tariff: [],
    unavailable_homes: [],
    visible_homes: 0,
    total_homes: 0,
    homes_with_meters: 0,
    series: [],
    attention: [],
    by_home: [],
    by_org: undefined,
    names: { homes: {}, orgs: {}, devices: {} },
  };
  let covW = 0;
  let cmpCur = 0;
  let cmpPrev = 0;
  let cmpMeters = 0;
  let cmpHomes = 0;
  const excluded: NonNullable<EnergySummary['comparison']>['excluded'] = [];
  const tzs = new Set<string>();
  for (const s of list) {
    out.mwh += s.mwh;
    if (s.forecast_mwh === null || s.forecast_mwh === undefined) out.forecast_mwh = null;
    else if (out.forecast_mwh !== null) out.forecast_mwh = (out.forecast_mwh ?? 0) + s.forecast_mwh;
    for (const c of s.cost_by_currency) {
      const x = out.cost_by_currency.find((y) => y.currency === c.currency);
      if (x) {
        x.amount += c.amount;
        x.homes += c.homes;
      } else out.cost_by_currency.push({ ...c });
    }
    for (const c of s.carbon) {
      const x = out.carbon.find((y) => y.region === c.region && y.gas === c.gas && y.factor_year === c.factor_year);
      if (x) {
        x.kg += c.kg;
        x.homes += c.homes;
      } else out.carbon.push({ ...c });
    }
    covW += s.coverage * s.meters;
    out.incomplete_hours += s.incomplete_hours;
    out.incomplete_meters += s.incomplete_meters;
    out.meters += s.meters;
    out.poe_meters += s.poe_meters;
    if (s.comparison) {
      cmpCur += s.comparison.cur_mwh;
      cmpPrev += s.comparison.prev_mwh;
      cmpMeters += s.comparison.meters;
      cmpHomes += s.comparison.homes;
      excluded.push(...s.comparison.excluded);
    }
    out.homes_without_tariff.push(...s.homes_without_tariff);
    out.unavailable_homes.push(...s.unavailable_homes);
    out.visible_homes += s.visible_homes;
    out.total_homes += s.total_homes;
    out.homes_with_meters += s.homes_with_meters;
    out.series = mergeSeries(out.series, s.series);
    out.attention.push(...s.attention);
    out.by_home.push(...s.by_home);
    if (s.tz) tzs.add(s.tz);
    if (s.mixed_tz) out.mixed_tz = true;
    if (s.show_carbon === false) out.show_carbon = false;
    Object.assign(out.names!.homes!, s.names?.homes);
    Object.assign(out.names!.orgs!, s.names?.orgs);
    Object.assign(out.names!.devices!, s.names?.devices);
  }
  if (tzs.size > 1) out.mixed_tz = true;
  out.coverage = out.meters ? covW / out.meters : 0;
  out.quality = out.incomplete_hours ? 'estimated' : 'measured';
  const first = list.find((s) => s.comparison)?.comparison;
  out.comparison =
    cmpPrev > 0 && first
      ? { prev_mwh: cmpPrev, cur_mwh: cmpCur, ratio: cmpPrev >= 1e6 ? (cmpCur - cmpPrev) / cmpPrev : null, meters: cmpMeters, homes: cmpHomes, prev_from: first.prev_from, prev_to: first.prev_to, excluded }
      : null;
  out.attention = sortAttention(out.attention);
  const orgs = new Set(out.by_home.map((h) => h.org_id ?? ''));
  if (orgs.size > 1) {
    const days = Math.max(1, list[0].by_home[0] && list[0].by_home[0].daily_avg_mwh ? list[0].by_home[0].mwh / list[0].by_home[0].daily_avg_mwh : 1);
    out.by_org = orgRowsOf(out.by_home, days);
  }
  return out;
}

// ---- 需要處理 ----

const LEVEL_RANK: Record<Attention['level'], number> = { bad: 0, high: 1, warn: 2, unk: 3 };

/** 排序：嚴重度 → 已開事故 → 數值（設計稿 r4 IA P2-10）。 */
export function sortAttention(list: Attention[]): Attention[] {
  const val = (a: Attention) => (a.allocated_w && a.rated_w ? a.allocated_w / a.rated_w : (a.power_mw ?? 0) / 1e9 + (a.meters ?? 0) / 1e6);
  return [...list].sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level] || (a.incident_id ? 0 : 1) - (b.incident_id ? 0 : 1) || val(b) - val(a));
}

// ---- 比較 ----

/** 依{leaf}表「比上月」排序用：不符比較條件的排最後。 */
export function compareKey(r: { comparison: { ratio: number | null } | null }): number {
  return r.comparison?.ratio ?? -Infinity;
}

/** 不比較的原因（「—」的說明）。 */
export function noCompareText(t: (k: string, o?: Record<string, unknown>) => string, reason: NoCompareReason | undefined, prevCoverage?: number): string {
  switch (reason) {
    case 'prev_coverage':
      return t('energy:cmp.why_prev_coverage', { pct: prevCoverage !== undefined ? Math.floor(prevCoverage * 100) : 0 });
    case 'meters_changed':
      return t('energy:cmp.why_meters_changed');
    case 'prev_too_small':
      return t('energy:cmp.why_prev_small');
    case 'unavailable':
      return t('energy:cmp.why_unavailable');
    default:
      return t('energy:cmp.why_no_prev');
  }
}

// ---- 量測點顯示名（SPEC #energy-ui-common） ----

export function meterName(t: (k: string, o?: Record<string, unknown>) => string, r: Pick<RankRow, 'kind' | 'name' | 'station_name' | 'port'>, opts: { crossSite?: boolean; homeName?: string } = {}): { name: string; sub?: string } {
  if (r.kind !== 'poe') return { name: r.name };
  const st = r.station_name ?? '';
  if (r.name) return { name: r.name, sub: t('energy:meter.poe_powered', { station: st, port: r.port }) };
  // 沒對到設備：Station 頁內寫「沒有對到設備」；跨{leaf}清單寫「〈{leaf}〉Station A 第 8 孔」
  return { name: opts.crossSite && opts.homeName ? t('energy:meter.port_of_home', { home: opts.homeName, station: st, port: r.port }) : t('energy:meter.port', { station: st, port: r.port }) };
}

// ---- CSV（SPEC #energy-ui-common：UTF-8 BOM、CRLF、= + - @ 開頭加 '、第一列免責、中文欄名帶單位） ----

export function csvCell(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  let s = String(v);
  if (typeof v === 'string' && /^[=+\-@]/.test(s)) s = "'" + s;
  if (/[",\r\n]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}

export function toCsv(disclaimer: string, header: string[], rows: (string | number | null | undefined)[][]): string {
  const lines = [csvCell(disclaimer), header.map(csvCell).join(','), ...rows.map((r) => r.map(csvCell).join(','))];
  return '\ufeff' + lines.join('\r\n') + '\r\n';
}

/** 檔名「用電-〈範圍〉-2026-10.csv」，暫定月份加「-暫定」；檔名不准有 / \ : * ? " < > |。 */
export function csvFileName(prefix: string, scopeName: string, period: string, provisional?: string): string {
  const clean = (s: string) => s.replace(/[/\\:*?"<>|]+/g, '_').trim();
  return `${clean(prefix)}-${clean(scopeName)}-${period}${provisional ? '-' + clean(provisional) : ''}.csv`;
}

/** kWh（CSV 保留三位小數）。 */
export const csvKwh = (mwh: number | null | undefined) => (mwh === null || mwh === undefined ? '' : (mwh / 1e6).toFixed(3));
/** 金額（CSV 保留兩位小數）。 */
export const csvMoney = (m: Money | undefined) => (m ? m.amount.toFixed(2) : '');

export function downloadText(name: string, text: string, type = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---- 其他 ----

/** 「本月到今天」的月份字串（YYYY-MM）。 */
export function monthOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function carbonKey(c: Pick<Carbon, 'region' | 'gas'>): string {
  return `${c.region}|${c.gas}`;
}

/** 幣別多於 3 種時只列 3 行，其餘收成「N 種幣別 ›」。 */
export function costLines<T extends Money>(list: T[], max = 3): { shown: T[]; more: number } {
  const sorted = [...list].sort((a, b) => b.amount - a.amount);
  return sorted.length > max ? { shown: sorted.slice(0, max - 1), more: sorted.length } : { shown: sorted, more: 0 };
}
